import React from 'react'
import ReactDOM from 'react-dom'
import styled, { css } from 'styled-components'

import { ErrorHandler } from '../ErrorHandler'
import { Title } from '../Title'

import { useFocus } from './useFocus'
import { useModalState } from './useModalState'

type Props = {
  disableFocusOnModalComponent?: boolean
  title?: string
  onCloseRequest?: () => void
  size?: ModalSize
  className?: string
  hasOuterScroll?: boolean
  compact?: boolean
  padding?: number
  strictClose?: boolean // Close only on X button click
}

type ModalSize = 'xl' | 'lg' | 'md'

export const Modal = ({
  children,
  disableFocusOnModalComponent,
  title,
  onCloseRequest,
  size,
  className,
  hasOuterScroll,
  compact,
  padding,
  strictClose,
}: WithChildren<Props>): JSX.Element => {
  const { handleClose, isTransitioning, transitionDurationMs } = useModalState(
    300,
    onCloseRequest
  )
  const elementToFocus = useFocus<HTMLDivElement>()

  return ReactDOM.createPortal(
    <Wrapper
      isTransitioning={isTransitioning}
      transitionDurationMs={transitionDurationMs}
      ref={!disableFocusOnModalComponent ? elementToFocus : undefined}
      role='dialog'
      tabIndex={0}
      onKeyDown={(e) => {
        if (!strictClose && e.keyCode === 27) {
          handleClose(e)
        }
      }}
    >
      <Backdrop
        onClick={(e) => {
          if (!strictClose) handleClose(e)
        }}
      />
      <ModalContainer
        hasOuterScroll={hasOuterScroll}
        onClick={(e) => {
          if (!strictClose) handleClose(e)
        }}
      >
        <ModalDialog
          data-role='modal'
          size={size || 'md'}
          onClick={(e) => {
            e.stopPropagation()
          }}
          className={className}
          hasOuterScroll={hasOuterScroll}
        >
          {!compact ? (
            <ModalHeader hasSeparator={!!title}>
              {title ? <Title size={4}>{title}</Title> : <div />}
              <ButtonClose onClick={handleClose}>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 32 32'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M18.1814 16L31.7569 29.5756L29.5751 31.7574L15.9995 18.1819L2.42401 31.7574L0.242188 29.5756L13.8177 16L0.242188 2.42449L2.42401 0.242676L15.9995 13.8182L29.5751 0.242676L31.7569 2.42449L18.1814 16Z'
                    fill='#0E1A2F'
                  />
                </svg>
              </ButtonClose>
            </ModalHeader>
          ) : null}

          <ErrorHandler>
            <ModalBody hasOuterScroll={hasOuterScroll} padding={padding}>
              {children}
            </ModalBody>
          </ErrorHandler>
        </ModalDialog>
      </ModalContainer>
    </Wrapper>,
    document.body
  )
}

const Backdrop = styled.div`
  display: block;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
`

type WrapperProps = {
  isTransitioning: boolean
  transitionDurationMs: number
}
const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;

  /* TRANSITION EFFECT */
  transition-duration: ${({ transitionDurationMs }) =>
    transitionDurationMs / 1000}s;
  transition-property: opacity;
  transition-timing-function: ease-in-out;
  opacity: ${({ isTransitioning }) => (isTransitioning ? 1 : 0)};
`

type ModalContainerProps = Pick<Props, 'hasOuterScroll'>

const ModalContainer = styled.div<ModalContainerProps>`
  ${({ hasOuterScroll }) =>
    hasOuterScroll
      ? css`
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: scroll;
          padding-top: 3rem;
        `
      : null}
`

type ModalDialogProps = Pick<Props, 'size' | 'hasOuterScroll'>

const ModalDialog = styled.div<ModalDialogProps>`
  display: block;

  background-color: #fff;
  width: ${({ size }) =>
    size === 'xl' ? '1150px' : size === 'lg' ? '900px' : '600px'};
  box-shadow: ${({ theme }) => theme.shadow.espansa};
  max-width: 90%;
  position: absolute;

  border-radius: 10px;

  ${({ hasOuterScroll }) =>
    hasOuterScroll
      ? css`
          top: 10%;
          left: 50%;
          transform: translate(-50%, 0);
          margin-bottom: 3rem;
        `
      : css`
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        `}
`

const ButtonClose = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
`

type ModalTitleHeaderProps = {
  hasSeparator?: boolean
}

const ModalHeader = styled.div<ModalTitleHeaderProps>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 2rem 2rem 0 2rem;

  ${({ hasSeparator }) =>
    hasSeparator
      ? css`
          padding-bottom: 1rem;
          border-bottom: 1px solid ${({ theme }) => theme.color.neutri.grigio};
        `
      : null}
`

type ModalBodyProps = Pick<Props, 'hasOuterScroll' | 'padding'>

const ModalBody = styled.div<ModalBodyProps>`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  overflow: auto;

  padding: ${({ padding }) =>
    padding !== undefined ? `${padding}rem` : '2rem'};

  ${({ hasOuterScroll }) =>
    hasOuterScroll
      ? css``
      : css`
          max-height: 200px;
          @media (min-height: 450px) {
            max-height: 300px;
          }
          @media (min-height: 600px) {
            max-height: 450px;
          }
          @media (min-height: 700px) {
            max-height: 550px;
          }
          @media (min-height: 900px) {
            max-height: 700px;
          }
        `}
`
